/**
 * SCHEMAS: FORMS
 * Schémas Zod pour la validation des formulaires
 */

import { z } from 'zod';

// =====================================================
// ENUMS
// =====================================================

export const formFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'select',
  'checkbox',
  'radio',
]);

/**
 * Type de formulaire
 */
export const formTypeSchema = z.enum(['simple', 'duo']);

// =====================================================
// FORM SCHEMAS
// =====================================================

/**
 * Schéma pour créer un formulaire
 */
/**
 * Exclusion mutuelle BPI / BED : un formulaire génère un acompte (BPI) OU un
 * solde (BED), jamais les deux. On refuse la combinaison `generates_bpi` ET
 * `generates_bed` simultanément à `true`. Garde applicative (miroir de la garde
 * UX dans le form builder) ; la colonne DB reste `DEFAULT false` sans CHECK
 * (migration 00176) pour ne pas bloquer une remédiation manuelle côté base.
 */
const bpiBedMutualExclusion = (data: { generates_bpi?: boolean; generates_bed?: boolean }) =>
  !(data.generates_bpi === true && data.generates_bed === true);
const bpiBedMutualExclusionError = {
  message: 'Un formulaire ne peut pas générer à la fois un BPI (acompte) et un BED (solde).',
  path: ['generates_bed'],
};

export const createFormSchema = z
  .object({
    name: z.string().min(1, 'Le nom est requis').max(255, 'Le nom est trop long'),
    slug: z
      .string()
      .max(255)
      .regex(
        /^[a-z0-9-]*$/,
        'Le slug ne peut contenir que des lettres minuscules, chiffres et tirets'
      )
      .optional()
      .or(z.literal('')),
    product_id: z.string().uuid().optional().nullable(),
    origin_id: z.string().uuid().optional().nullable(),
    is_active: z.boolean().optional(),
    form_type: formTypeSchema.optional(),
    generates_bpi: z.boolean().optional(),
    generates_bed: z.boolean().optional(),
  })
  .refine(bpiBedMutualExclusion, bpiBedMutualExclusionError);

export type CreateFormInput = z.infer<typeof createFormSchema>;

/**
 * Schéma pour mettre à jour un formulaire
 */
export const updateFormSchema = z
  .object({
    name: z.string().min(1, 'Le nom est requis').max(255, 'Le nom est trop long').optional(),
    product_id: z.string().uuid().optional().nullable().or(z.literal('')),
    origin_id: z.string().uuid().optional().nullable().or(z.literal('')),
    is_active: z.boolean().optional(),
    form_type: formTypeSchema.optional(),
    generates_bpi: z.boolean().optional(),
    generates_bed: z.boolean().optional(),
  })
  .refine(bpiBedMutualExclusion, bpiBedMutualExclusionError);

export type UpdateFormInput = z.infer<typeof updateFormSchema>;

// =====================================================
// FORM FIELD SCHEMAS
// =====================================================

/**
 * Schéma pour créer un champ de formulaire
 */
export const createFormFieldSchema = z.object({
  form_id: z.string().uuid('ID de formulaire invalide'),
  label: z.string().min(1, 'Le label est requis').max(255, 'Le label est trop long'),
  field_type: formFieldTypeSchema,
  placeholder: z.string().max(255, 'Le placeholder est trop long').optional().nullable(),
  is_required: z.boolean().optional(),
  options: z.array(z.string()).optional().nullable(),
  order_index: z.number().int().min(0, "L'ordre doit être positif"),
});

export type CreateFormFieldInput = z.infer<typeof createFormFieldSchema>;

/**
 * Schéma pour mettre à jour un champ de formulaire
 */
export const updateFormFieldSchema = createFormFieldSchema.partial().omit({ form_id: true });

export type UpdateFormFieldInput = z.infer<typeof updateFormFieldSchema>;

// =====================================================
// FORM SUBMISSION SCHEMAS
// =====================================================

/**
 * Schéma pour créer une soumission de formulaire
 * Note: Les données sont validées dynamiquement selon les champs du formulaire
 */
export const createFormSubmissionSchema = z.object({
  form_id: z.string().uuid('ID de formulaire invalide'),
  data: z.record(z.string(), z.any()), // Validé dynamiquement
  contact_id: z.string().uuid().optional().nullable(),
});

export type CreateFormSubmissionInput = z.infer<typeof createFormSubmissionSchema>;

// =====================================================
// VALIDATION HELPERS
// =====================================================

/**
 * Crée un schéma de validation dynamique pour les données d'un formulaire
 * Messages d'erreur en français
 */
export function createDynamicFormSchema(
  fields: Array<{
    id: string;
    label: string;
    field_type: string;
    is_required: boolean;
    options?: string[];
    contact_field_map?: string;
    target_role?: string | null;
  }>
) {
  const shape: Record<string, z.ZodTypeAny> = {};

  // Fonction utilitaire pour normaliser undefined/null en chaîne vide
  const normalizeToString = (val: unknown) => (val === undefined || val === null ? '' : val);

  fields.forEach((field) => {
    let fieldSchema: z.ZodTypeAny;
    const label = field.label;

    // Définir le schéma selon le type de champ
    switch (field.field_type) {
      case 'email':
        if (field.is_required) {
          fieldSchema = z.preprocess(
            normalizeToString,
            z.string().min(1, `${label} est requis`).email(`${label} : format d'email invalide`)
          );
        } else {
          // Pour les champs optionnels, permettre vide ou email valide
          fieldSchema = z
            .preprocess(normalizeToString, z.string())
            .refine((val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
              message: `${label} : format d'email invalide`,
            });
        }
        break;

      case 'phone':
        // PhoneInput peut retourner undefined - on normalise en string vide
        if (field.is_required) {
          fieldSchema = z.preprocess(normalizeToString, z.string().min(1, `${label} est requis`));
        } else {
          fieldSchema = z.preprocess(normalizeToString, z.string());
        }
        break;

      case 'number':
        if (field.is_required) {
          fieldSchema = z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : val),
            z.coerce.number({ message: `${label} doit être un nombre` })
          );
        } else {
          fieldSchema = z.preprocess(
            (val) => (val === '' || val === undefined || val === null ? undefined : val),
            z.coerce
              .number({ message: `${label} doit être un nombre` })
              .optional()
              .nullable()
          );
        }
        break;

      case 'date':
        if (field.is_required) {
          fieldSchema = z.preprocess(
            normalizeToString,
            z
              .string()
              .min(1, `${label} est requis`)
              .regex(/^\d{4}-\d{2}-\d{2}$/, `${label} : format de date invalide`)
          );
        } else {
          // Pour les champs optionnels, permettre vide ou date valide
          fieldSchema = z
            .preprocess(normalizeToString, z.string())
            .refine((val) => val === '' || /^\d{4}-\d{2}-\d{2}$/.test(val), {
              message: `${label} : format de date invalide`,
            });
        }
        break;

      case 'select':
      case 'radio':
        if (field.options && field.options.length > 0) {
          if (field.is_required) {
            // Pour un select requis, on vérifie que la valeur est une des options
            fieldSchema = z
              .preprocess(normalizeToString, z.string())
              .refine((val) => field.options!.includes(val), {
                message: `${label} : veuillez sélectionner une option`,
              });
          } else {
            // Pour les selects optionnels, permettre vide ou une des options
            fieldSchema = z
              .preprocess(normalizeToString, z.string())
              .refine((val) => val === '' || field.options!.includes(val), {
                message: `${label} : option invalide`,
              });
          }
        } else {
          fieldSchema = field.is_required
            ? z.preprocess(normalizeToString, z.string().min(1, `${label} est requis`))
            : z.preprocess(normalizeToString, z.string());
        }
        break;

      case 'checkbox':
        if (field.is_required) {
          fieldSchema = z.preprocess(
            (val) => (val === undefined || val === null ? false : val),
            z.boolean().refine((val) => val === true, {
              message: `${label} doit être coché`,
            })
          );
        } else {
          fieldSchema = z
            .preprocess((val) => (val === undefined || val === null ? false : val), z.boolean())
            .optional();
        }
        break;

      case 'textarea':
        if (field.is_required) {
          fieldSchema = z.preprocess(
            normalizeToString,
            z
              .string()
              .min(1, `${label} est requis`)
              .max(5000, `${label} : texte trop long (max 5000 caractères)`)
          );
        } else {
          fieldSchema = z.preprocess(
            normalizeToString,
            z.string().max(5000, `${label} : texte trop long (max 5000 caractères)`)
          );
        }
        break;

      case 'text':
      default:
        if (field.is_required) {
          fieldSchema = z.preprocess(
            normalizeToString,
            z
              .string()
              .min(1, `${label} est requis`)
              .max(255, `${label} : texte trop long (max 255 caractères)`)
          );
        } else {
          fieldSchema = z.preprocess(
            normalizeToString,
            z.string().max(255, `${label} : texte trop long (max 255 caractères)`)
          );
        }
        break;
    }

    shape[field.id] = fieldSchema;
  });

  // Duo : un email partagé pilote/copilote est AUTORISÉ (Retour A réunion
  // 07/07). La dédup contacts est en clé triple email+prénom+nom (migration
  // 00152) : deux équipiers sur la même boîte donnent deux contacts distincts,
  // et le module paiement identifie le payeur par contact_id, pas par email.
  return z.object(shape);
}
